// Revalidation service to handle cache invalidation for content updates
import { articlesService } from './content-service';
import { authenticatedFetch } from './auth-utils';

interface RevalidationRequest {
  type: 'article' | 'author' | 'featured';
  articleId?: string;
  slug?: string;
  authorId?: string;
}

export class RevalidationService {
  private static instance: RevalidationService;

  public static getInstance(): RevalidationService {
    if (!RevalidationService.instance) {
      RevalidationService.instance = new RevalidationService();
    }
    return RevalidationService.instance;
  }

  /**
   * Trigger revalidation for article updates
   */
  async revalidateArticle(slug: string, articleId?: string): Promise<void> {
    try {
      const response = await authenticatedFetch('/api/revalidate', {
        method: 'POST',
        body: JSON.stringify({
          type: 'article',
          slug,
          articleId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Revalidation failed: ${response.statusText}`);
      }

      const result = await response.json();
      } catch (error) {
      // Don't throw - revalidation failure shouldn't break the user experience
    }
  }

  /**
   * Trigger revalidation for author profile updates (optimized for performance)
   */
  async revalidateAuthor(authorId: string): Promise<void> {
    try {
      // Get all article paths by this author for targeted revalidation
      const articlePaths = await articlesService.getArticlePathsByAuthor(authorId);
      
      if (articlePaths.length === 0) {
        return;
      }

      // Batch revalidate all article pages for this author
      const revalidationPromises = articlePaths.map(article =>
        authenticatedFetch('/api/revalidate', {
          method: 'POST',
          body: JSON.stringify({
            type: 'article',
            slug: article.slug,
            articleId: article.id,
          }),
        })
      );

      // Also revalidate general pages that might show author info
      revalidationPromises.push(
        authenticatedFetch('/api/revalidate', {
          method: 'POST',
          body: JSON.stringify({
            type: 'featured', // This handles /, /articles, etc.
          }),
        })
      );

      const responses = await Promise.all(revalidationPromises);
      
      // Check for any failures
      const failures = responses.filter(response => !response.ok);
      if (failures.length > 0) {
        } else {
        }
    } catch (error) {
      // Don't throw - revalidation failure shouldn't break the user experience
    }
  }

  /**
   * Trigger revalidation for featured content updates
   */
  async revalidateFeatured(): Promise<void> {
    try {
      const response = await authenticatedFetch('/api/revalidate', {
        method: 'POST',
        body: JSON.stringify({
          type: 'featured',
        }),
      });

      if (!response.ok) {
        throw new Error(`Revalidation failed: ${response.statusText}`);
      }

      const result = await response.json();
      } catch (error) {
      // Don't throw - revalidation failure shouldn't break the user experience
    }
  }

  /**
   * Batch revalidation for multiple types
   */
  async revalidateBatch(requests: RevalidationRequest[]): Promise<void> {
    const promises = requests.map(request => {
      switch (request.type) {
        case 'article':
          if (request.slug) {
            return this.revalidateArticle(request.slug, request.articleId);
          }
          break;
        case 'author':
          if (request.authorId) {
            return this.revalidateAuthor(request.authorId);
          }
          break;
        case 'featured':
          return this.revalidateFeatured();
      }
      return Promise.resolve();
    });

    await Promise.all(promises);
  }
}

// Export singleton instance
export const revalidationService = RevalidationService.getInstance();

